import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/**
 * @typedef {object} CompanyProfile - Профиль компании.
 * @property {number} id - ID компании.
 * @property {string} name - Название компании.
 * @property {object} active_license - Данные об активной лицензии.
 * @property {Array<string>} active_license.modules_codes - Коды доступных модулей.
 */

/**
 * @typedef {object} UserPermissions - Права доступа пользователя в компании.
 * @property {string} base_permission - Базовый уровень доступа.
 * @property {object} additional_permissions - Дополнительные права.
 */

/**
 * Хранилище для состояния текущей выбранной компании.
 * Обеспечивает реактивное представление данных для UI.
 */
export const useCurrentCompanyStore = defineStore('currentCompany', () => {
	// --- STATE ---
	const isLoading = ref(false)
	/** @type {import('vue').Ref<CompanyProfile|null>} */
	const companyProfile = ref(null)
	/** @type {import('vue').Ref<UserPermissions|null>} */
	const currentUserCurrentCompanyPermission = ref(null)

	// --- GETTERS ---
	const companyActiveLicense = computed(() => companyProfile.value?.active_license ?? null)

	// --- ACTIONS ---
	/**
	 * Устанавливает профиль компании.
	 * @param {CompanyProfile} profile
	 */
	function setCompanyProfile(profile) {
		companyProfile.value = profile
	}

	/**
	 * Устанавливает права доступа пользователя для текущей компании.
	 * @param {UserPermissions} permissions
	 */
	function setCompanyPermissions(permissions) {
		currentUserCurrentCompanyPermission.value = permissions
	}

	/**
	 * Очищает данные о текущей компании.
	 */
	function clearCompanyData() {
		companyProfile.value = null
		currentUserCurrentCompanyPermission.value = null
	}

	/**
	 * Устанавливает статус загрузки данных.
	 * @param {boolean} status
	 */
	function setIsLoading(status) {
		isLoading.value = status
	}

	return {
		// State
		isLoading,
		companyProfile,
		currentUserCurrentCompanyPermission,
		// Getters
		companyActiveLicense,
		// Actions
		setCompanyProfile,
		setCompanyPermissions,
		clearCompanyData,
		setIsLoading,
	}
})
